// src/components/Finanzas/DashboardFinanciero.jsx
// Dashboard admin/contable con métricas globales del módulo financiero.
// Recicla patrón de ReporteViaticos (cards + charts + tabla detalle).

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert } from 'react-bootstrap';
import { FaChartLine, FaWallet, FaMoneyBillWave, FaExclamationTriangle } from 'react-icons/fa';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { Alertassw } from '../Alertas/Alertassw';
import {
  cuentasBolsillosPersonales,
  listarRelaciones,
  listarComprobantes
} from '../../api/finanzas';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const DashboardFinanciero = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const [bols, rels, syncErr, syncNoConf] = await Promise.all([
        cuentasBolsillosPersonales(),
        listarRelaciones({ conditions: { activo: true }, sort: { createdAt: -1 } }),
        listarComprobantes({ syncStatus: 'SYNC_ERROR' }),
        listarComprobantes({ syncStatus: 'SYNC_NO_CONFIGURADO' })
      ]);

      const bolsillos = bols?.data?.data || [];
      const relaciones = rels?.data?.data || [];
      const errores = syncErr?.data?.data || [];
      const noConf = syncNoConf?.data?.data || [];

      // Agrupar bolsillos por función
      const porFuncion = {};
      bolsillos.forEach(b => {
        const f = b.funcion || 'Otros';
        if (!porFuncion[f]) porFuncion[f] = { cantidad: 0, saldoTotal: 0 };
        porFuncion[f].cantidad += 1;
        porFuncion[f].saldoTotal += b.saldoActual || 0;
      });

      // Relaciones por estado
      const porEstado = relaciones.reduce((acc, r) => {
        acc[r.estado] = (acc[r.estado] || 0) + 1;
        return acc;
      }, {});

      // Top 5 bolsillos en deuda
      const enDeuda = bolsillos
        .filter(b => b.saldoActual < 0)
        .sort((a, b) => a.saldoActual - b.saldoActual)
        .slice(0, 5);

      setData({
        bolsillos,
        relaciones,
        errores,
        noConf,
        porFuncion,
        porEstado,
        enDeuda,
        totales: {
          bolsillosActivos: bolsillos.filter(b => b.activo).length,
          bolsillosEnDeuda: enDeuda.length,
          saldoTotal: bolsillos.reduce((s, b) => s + (b.saldoActual || 0), 0),
          relacionesAbiertas: relaciones.filter(r => r.estado === 'BORRADOR').length,
          relacionesPendientesSync: porEstado.SYNC_ERROR || 0,
          erroresSync: errores.length,
          comprobantesSinConfig: noConf.length
        }
      });
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudo cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">Sin datos</Alert>
      </Container>
    );
  }

  // Datos charts
  const dataChartFuncion = {
    labels: Object.keys(data.porFuncion),
    datasets: [{
      data: Object.values(data.porFuncion).map(v => v.saldoTotal),
      backgroundColor: ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6c757d', '#0dcaf0', '#fd7e14']
    }]
  };

  const dataChartEstados = {
    labels: Object.keys(data.porEstado),
    datasets: [{
      label: 'Relaciones',
      data: Object.values(data.porEstado),
      backgroundColor: '#0d6efd'
    }]
  };

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaChartLine className="me-2" />
            Dashboard Financiero
          </h2>
          <p className="text-muted">Vista global del módulo financiero</p>
        </Col>
      </Row>

      {/* Cards de métricas */}
      <Row className="mb-4 g-2">
        <Col md={3}>
          <Card className="shadow-sm h-100 w-100">
            <Card.Body className="text-center w-100">
              <FaWallet className="text-primary mb-2" size={30} />
              <h6 className="text-muted">Bolsillos activos</h6>
              <h3 className="fw-bold text-primary mb-0">{data.totales.bolsillosActivos}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm h-100 w-100">
            <Card.Body className="text-center w-100">
              <FaMoneyBillWave className="text-success mb-2" size={30} />
              <h6 className="text-muted">Saldo total en circulación</h6>
              <h4 className="fw-bold text-success mb-0">{formatoCOP(data.totales.saldoTotal)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={`shadow-sm h-100 w-100 ${data.totales.bolsillosEnDeuda > 0 ? 'border-danger' : ''}`}>
            <Card.Body className="text-center w-100">
              <FaExclamationTriangle className={`mb-2 ${data.totales.bolsillosEnDeuda > 0 ? 'text-danger' : 'text-muted'}`} size={30} />
              <h6 className="text-muted">Bolsillos en deuda</h6>
              <h3 className={`fw-bold mb-0 ${data.totales.bolsillosEnDeuda > 0 ? 'text-danger' : 'text-muted'}`}>
                {data.totales.bolsillosEnDeuda}
              </h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={`shadow-sm h-100 w-100 ${data.totales.erroresSync > 0 ? 'border-warning' : ''}`}>
            <Card.Body className="text-center w-100">
              <FaExclamationTriangle className={`mb-2 ${data.totales.erroresSync > 0 ? 'text-warning' : 'text-muted'}`} size={30} />
              <h6 className="text-muted">Errores de sync</h6>
              <h3 className={`fw-bold mb-0 ${data.totales.erroresSync > 0 ? 'text-warning' : 'text-muted'}`}>
                {data.totales.erroresSync}
              </h3>
              <small className="text-muted">
                {data.totales.comprobantesSinConfig} sin config
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4 g-3">
        <Col md={6}>
          <Card className="shadow-sm  w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Saldos por función de bolsillo</h6>
            </Card.Header>
            <Card.Body className="w-100">
              {Object.keys(data.porFuncion).length === 0 ? (
                <Alert variant="info" className="mb-0">Sin datos</Alert>
              ) : (
                <Pie data={dataChartFuncion} options={{ responsive: true, plugins: { legend: { position: 'right' }}}}/>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm  w-100">
            <Card.Header className="bg-light w-100">
              <h6 className="mb-0">Relaciones de gastos por estado</h6>
            </Card.Header>
            <Card.Body className="w-100">
              {Object.keys(data.porEstado).length === 0 ? (
                <Alert variant="info" className="mb-0">Sin relaciones</Alert>
              ) : (
                <Bar data={dataChartEstados} options={{
                  responsive: true,
                  plugins: { legend: { display: false }},
                  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 }}}
                }}/>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top bolsillos en deuda */}
      {data.enDeuda.length > 0 && (
        <Card className="shadow-sm mb-3 border-danger w-100">
          <Card.Header className="bg-danger text-white w-100">
            <h6 className="mb-0">⚠️ Bolsillos en deuda (top 5)</h6>
          </Card.Header>
          <Card.Body className="w-100">
            <Table hover responsive size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Bolsillo</th>
                  <th>Responsable</th>
                  <th>Función</th>
                  <th className="text-end">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.enDeuda.map((b) => (
                  <tr key={b._id}>
                    <td><code>{b.codigo}</code> {b.nombre}</td>
                    <td><small>{b.responsable?.nombre || b.responsable?.nombres || '—'}</small></td>
                    <td><Badge bg="info">{b.funcion || 'Otros'}</Badge></td>
                    <td className="text-end fw-bold text-danger">{formatoCOP(b.saldoActual)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Errores de sync resumen */}
      {(data.totales.erroresSync > 0 || data.totales.comprobantesSinConfig > 0) && (
        <Alert variant="warning">
          <FaExclamationTriangle className="me-2" />
          <strong>Atención:</strong> hay {data.totales.erroresSync} comprobante(s) con error de sync y{' '}
          {data.totales.comprobantesSinConfig} sin configurar.{' '}
          <a href="/ErroresSincronizacion" className="alert-link">Ir a la administración →</a>
        </Alert>
      )}
    </Container>
  );
};

export default DashboardFinanciero;
